import { z } from 'zod'
import { MarketMakerEngagementsStatusEnumValues, MarketMakerKpiTypeEnumValues } from '@forgd/shared/enums'
import { MarketMakerSchema } from '../market-maker'
import { EngagementOptionEnumValues } from '@forgd/shared/enums'

export const EngagementHistorySchema = z.object({
  marketMaker: MarketMakerSchema.pick({
    id: true,
    name: true,
    imageUrl: true,
  }),
  engagement: z.object({
    id: z.string(),
    status: z.enum(MarketMakerEngagementsStatusEnumValues),
    firstEngagementDate: z.string().date().nullable(),
    lastEngagementDate: z.string().date().nullable(),
    totalEngagements: z.number().safe().nullable(),
  }),
})

export type EngagementHistory = z.infer<typeof EngagementHistorySchema>

// Get Project Engagements Details
export const GetEngagementsHistoryResult = z.array(EngagementHistorySchema)
export type GetEngagementsHistoryResultType = z.infer<typeof GetEngagementsHistoryResult>

export const PerformanceInsightsSchema = z.object({
  makerTakerPercentage: z.number().safe(),
  allMmBidAsk200bpsPercentage: z.number().safe(),
  avgSpread: z.number().safe().nullable(),
})

export const PerformanceSummarySchema = z.array(
  z.object({
    mmId: z.string(),
    mmName: z.string(),
    mmImageUrl: z.string().nullable(),
    avgSpread: z.number().safe().nullable(),
    avgBidAskDepth50: z.number().safe().nullable(),
    avgBidAskDepth100: z.number().safe().nullable(),
    avgBidAskDepth200: z.number().safe().nullable(),
  }),
)

export const KPIDataSchema = z.object({
  activeDex: z.number().safe(),
  activeCex: z.number().safe(),
  avg24hVolume: z.number().safe().nullable(),
  avgDepth: z.number().safe().nullable(),
})

export const KPISpreadSchema = z.object({
  spread: z.number().safe().nullable(),
})

export const MmmBaseQuery = z.object({
  date: z.string().date(),
  daysLookback: z.coerce.number().int().min(1).max(365).optional(),
  exchangeId: z.string().optional(),
  tradingPair: z.string().optional(),
})

export type MmmBaseQueryType = z.infer<typeof MmmBaseQuery>

export const MmmBaseMmQuery = MmmBaseQuery.extend({
  marketMakerId: z.string().optional(),
})

export type MmmBaseMmQueryType = z.infer<typeof MmmBaseMmQuery>

export const GetDashboardDataResult = z.object({
  performanceInsights: PerformanceInsightsSchema.nullish(),
  performanceSummary: PerformanceSummarySchema.nullish(),
  kpiData: KPIDataSchema.nullish(),
  noExchangeTradingActivity: z.boolean().nullish(),
  noData: z.boolean().nullish(),
})

export type GetDashboardDataResultType = z.infer<typeof GetDashboardDataResult>

export const LiquiditySideSchema = z.enum(['bidAsk', 'bid', 'ask'])
export type LiquiditySide = z.infer<typeof LiquiditySideSchema>

export const LiquidityDepthSchema = z.enum(['50', '100', '200'])
export type LiquidityDepth = z.infer<typeof LiquidityDepthSchema>

export const GetDepthBreakdownQuery = MmmBaseMmQuery.extend({
  liquiditySide: LiquiditySideSchema.optional(),
  liquidityDepth: LiquidityDepthSchema.optional(),
  includeSeries: z.coerce.boolean().optional(),
})

export type GetDepthBreakdownQuery = z.infer<typeof GetDepthBreakdownQuery>

export const DepthBreakdownSchema = z.object({
  date: z.string().date(),
  marketMakers: z.number().safe().nullable(),
  allOthers: z.number().safe().nullable(),
  total: z.number().safe().nullable(),
  marketMakersPercentage: z.number().safe().nullable(),
  allOthersPercentage: z.number().safe().nullable(),
  hasMarketTotals: z.boolean(),
})

export type DepthBreakdown = z.infer<typeof DepthBreakdownSchema>

export const BreakdownSeriesPointSchema = z.object({
  date: z.string().date(),
  value: z.number().safe().nullable(),
  percentage: z.number().safe().nullable(),
})

export type BreakdownSeriesPoint = z.infer<typeof BreakdownSeriesPointSchema>

export const BreakdownSeriesSchema = z.object({
  id: z.string(),
  label: z.string(),
  avgValue: z.number().safe().nullable(),
  avgPercentage: z.number().safe().nullable(),
  dailyBreakdown: z.array(BreakdownSeriesPointSchema),
})

export type BreakdownSeries = z.infer<typeof BreakdownSeriesSchema>

export const GetDepthBreakdownResult = z.object({
  avgBreakdown: DepthBreakdownSchema.omit({ date: true, hasMarketTotals: true }),
  dailyBreakdown: z.array(DepthBreakdownSchema),
  isOverreporting: z.boolean(),
  series: z.array(BreakdownSeriesSchema).optional(),
})

export type GetDepthBreakdownResult = z.infer<typeof GetDepthBreakdownResult>

export const FillVolumeSideSchema = z.enum(['total', 'maker', 'taker'])
export type FillVolumeSide = z.infer<typeof FillVolumeSideSchema>

export const GetFillVolumeBreakdownQuery = MmmBaseMmQuery.extend({
  fillVolumeSide: FillVolumeSideSchema,
  includeSeries: z.coerce.boolean().optional(),
})

export type GetFillVolumeBreakdownQuery = z.infer<typeof GetFillVolumeBreakdownQuery>

export const GetFillVolumeBreakdownResult = z.object({
  avgBreakdown: DepthBreakdownSchema.omit({ date: true, hasMarketTotals: true }),
  dailyBreakdown: z.array(DepthBreakdownSchema),
  isOverreporting: z.boolean(),
  series: z.array(BreakdownSeriesSchema).optional(),
})

export type FillVolumeBreakdown = z.infer<typeof DepthBreakdownSchema>

export type GetFillVolumeBreakdownResult = z.infer<typeof GetFillVolumeBreakdownResult>

export const LoanUtilizationPointSchema = z.object({
  date: z.string().date(),
  utilizationPercent: z.number().safe().nullable(),
})

export type LoanUtilizationPoint = z.infer<typeof LoanUtilizationPointSchema>

export const LoanUtilizationSeriesSchema = z.object({
  id: z.string(),
  label: z.string(),
  breakdown: z.array(LoanUtilizationPointSchema),
})

export type LoanUtilizationSeries = z.infer<typeof LoanUtilizationSeriesSchema>

export const LoanUtilizationAggregateSchema = z.object({
  breakdown: z.array(LoanUtilizationPointSchema),
  currentUtilizationPercent: z.number().safe().nullable(),
})

export type LoanUtilizationAggregate = z.infer<typeof LoanUtilizationAggregateSchema>

export const GetLoanUtilizationDepthQuery = MmmBaseMmQuery.extend({
  liquiditySide: LiquiditySideSchema.optional(),
  liquidityDepth: LiquidityDepthSchema.optional(),
})

export type GetLoanUtilizationDepthQuery = z.infer<typeof GetLoanUtilizationDepthQuery>

export const GetLoanUtilizationFillVolumeQuery = MmmBaseMmQuery.extend({
  fillVolumeSide: FillVolumeSideSchema,
})

export type GetLoanUtilizationFillVolumeQuery = z.infer<typeof GetLoanUtilizationFillVolumeQuery>

export const GetLoanUtilizationResult = z.object({
  aggregate: LoanUtilizationAggregateSchema,
  series: z.array(LoanUtilizationSeriesSchema),
})

export type GetLoanUtilizationResult = z.infer<typeof GetLoanUtilizationResult>

export const GetOrderBookEvolutionQuery = MmmBaseQuery.extend({
  depth: LiquidityDepthSchema,
})

export type GetOrderBookEvolutionQuery = z.infer<typeof GetOrderBookEvolutionQuery>

export const OrderBookEvolutionSchema = z.object({
  date: z.string(),
  bid: z.number().safe(),
  ask: z.number().safe(),
})

export type OrderBookEvolution = z.infer<typeof OrderBookEvolutionSchema>

export const DailyOrderBookEvolutionSchema = OrderBookEvolutionSchema.extend({
  bidAsk: z.number().safe(),
  change: z.number().safe().nullable(),
})

export type DailyOrderBookEvolution = z.infer<typeof DailyOrderBookEvolutionSchema>

export const GetOrderBookEvolutionResult = z.object({
  avgBid: z.number().safe().nullable(),
  avgAsk: z.number().safe().nullable(),
  breakdown: z.array(OrderBookEvolutionSchema),
  dailyBreakdown: z.array(DailyOrderBookEvolutionSchema),
})

export type GetOrderBookEvolutionResult = z.infer<typeof GetOrderBookEvolutionResult>

export const SpreadBreakdownSchema = z.object({
  date: z.string(),
  spread: z.number().safe(),
})

export type SpreadBreakdown = z.infer<typeof SpreadBreakdownSchema>

export const DailySpreadBreakdownSchema = z.object({
  date: z.string(),
  widest: z.number().safe().nullable(),
  tightest: z.number().safe().nullable(),
  avg: z.number().safe().nullable(),
  opening: z.number().safe().nullable().optional(),
  closing: z.number().safe().nullable().optional(),
  changePercent: z.number().safe().nullable(),
})

export const GetAverageSpreadResult = z.object({
  avgSpread: z.number().safe().nullable(),
  dailyBreakdown: z.array(DailySpreadBreakdownSchema),
  breakdown: z.array(
    z.object({
      date: z.string(),
      spread: z.number().safe().nullable(),
    }),
  ),
})

export type GetAverageSpreadResult = z.infer<typeof GetAverageSpreadResult>

export const TokenPriceBreakdownSchema = z.object({
  date: z.string(),
  price: z.number().safe().nullable(),
})

export type TokenPriceBreakdown = z.infer<typeof TokenPriceBreakdownSchema>

export const DailyTokenPriceBreakdownSchema = z.object({
  date: z.string().date(),
  startOfDay: z.number().safe().nullable(),
  endOfDay: z.number().safe().nullable(),
  changePercent: z.number().safe().nullable(),
})

export const GetTokenPriceResult = z
  .object({
    current: z
      .object({
        price: z.number().safe(),
        priceChangePercentage24h: z.number().safe(),
      })
      .nullable(),
    breakdown: z.array(TokenPriceBreakdownSchema),
    dailyBreakdown: z.array(DailyTokenPriceBreakdownSchema),
  })
  .nullable()

export type GetTokenPriceResult = z.infer<typeof GetTokenPriceResult>

export const MarketMakerComparisonSchema = z.object({
  mmId: z.string(),
  mmName: z.string(),
  mmImageUrl: z.string().nullable(),
  engagementType: z.enum(EngagementOptionEnumValues),
  avgFillVolumeTotal: z.number().safe(),
  avgFillVolumeMaker: z.number().safe(),
  avgFillVolumeTaker: z.number().safe(),
  avgBidDepth50: z.number().safe(),
  avgAskDepth50: z.number().safe(),
  avgBidAskDepth50: z.number().safe(),
  avgBidDepth100: z.number().safe(),
  avgAskDepth100: z.number().safe(),
  avgBidAskDepth100: z.number().safe(),
  avgBidDepth200: z.number().safe(),
  avgAskDepth200: z.number().safe(),
  avgBidAskDepth200: z.number().safe(),
  avgSpread: z.number().safe().nullable(),
  avgVolumeBasedLoanUtil: z.number().safe().nullable(),
  avgLiquidityBasedLoanUtil: z.number().safe().nullable(),
  totalDaysTracked: z.number().safe(),
  loanSize: z.number().safe().nullable(),
})

export const MarketMakerComparisonWithOverreportingSchema = MarketMakerComparisonSchema.extend({
  overreportingFillVolumeMaker: z.boolean(),
  overreportingFillVolumeTaker: z.boolean(),
  overreportingAskDepth200: z.boolean(),
  overreportingBidDepth200: z.boolean(),
})

export type MarketMakerComparison = z.infer<typeof MarketMakerComparisonSchema>

export const GetMarketMakerComparisonResult = z.array(MarketMakerComparisonWithOverreportingSchema)

export type GetMarketMakerComparisonResult = z.infer<typeof GetMarketMakerComparisonResult>

export const GetMarketMakerEngagementDetailsResult = z
  .object({
    mmComparison: MarketMakerComparisonSchema,
    exchangesActivity: z.object({
      activeDex: z.number().safe(),
      activeCex: z.number().safe(),
    }),
    percentageOfTotalVolume: z.number().safe(),
    percentageOfTotalLiquidity: z.number().safe(),

    activeEngagementsCount: z.number().safe(),
    averageFDV: z.number().safe(),
  })
  .nullable()

export type GetMarketMakerEngagementDetailsResult = z.infer<typeof GetMarketMakerEngagementDetailsResult>

// Market Maker Uptime Breakdown
export const GetUptimeCalculationsQuery = MmmBaseQuery.omit({
  exchangeId: true,
  tradingPair: true,
})

export type GetUptimeCalculationsQuery = z.infer<typeof GetUptimeCalculationsQuery>

// Daily breakdown schema for a single day's metrics
export const UptimeBreakdownSchema = z.object({
  date: z.string(),
  reportedDepth50: z.number().safe().nullable(),
  reportedDepth100: z.number().safe().nullable(),
  reportedDepth200: z.number().safe().nullable(),
  reportedSpread: z.number().safe().nullable(),
  targetDepth50: z.number().nullish(),
  targetDepth100: z.number().nullish(),
  targetDepth200: z.number().nullish(),
  targetSpread: z.number().nullish(),
  achievedDepth50: z.boolean().nullable(),
  achievedDepth100: z.boolean().nullable(),
  achievedDepth200: z.boolean().nullable(),
  achievedSpread: z.boolean().nullable(),
  uptimeRatio: z.number().safe().nullable(),
})

// Performance comparison schema for aggregated metrics
export const UptimePerformanceSchema = z.object({
  avgBidAskSpread: z.number().safe().nullable(),
  avgDepth50: z.number().safe().nullable(),
  avgDepth100: z.number().safe().nullable(),
  avgDepth200: z.number().safe().nullable(),
  avgUptime: z.number().safe().nullable(),
})

// Main schema for market maker uptime breakdown
export const UptimeCalculationsSchema = z.object({
  marketMakerId: z.string(),
  marketMakerName: z.string(),
  engagementType: z.enum(EngagementOptionEnumValues),
  imageUrl: z.string().nullable(),
  kpiEngagementType: z.enum(MarketMakerKpiTypeEnumValues),
  uptimeObligation: z.number().safe(),
  performance: UptimePerformanceSchema,
  breakdown: z.array(UptimeBreakdownSchema),
})

export type UptimeCalculations = z.infer<typeof UptimeCalculationsSchema>

export const GetUptimeCalculationsResult = z.array(UptimeCalculationsSchema)

export type GetUptimeCalculationsResult = z.infer<typeof GetUptimeCalculationsResult>
